import React, { useMemo, useRef } from "react";
import linking from "./LinkingConfiguration";
// react-navigation
import { DefaultTheme, NavigationContainer } from "@react-navigation/native";
import { createNativeStackNavigator } from "@react-navigation/native-stack";
import { navigationRef } from "../RootNavigation";

// types
import { RootStackParamList } from "./types";

// screens
import GroupReservation from "screens/GroupReservation/GroupReservation";
import Legal from "screens/Legal/Legal";

// store
import { setTheme } from "features/slices/themeSlice";
import { useAppDispatch } from "hooks/useAppDispatch";
import { useAppSelector } from "hooks/useAppSelector";

import { isDev } from "config/env";
import useIsRunningInBallpark from "hooks/useIsRunningInBallpark";
import { useEffect } from "react";
import toast from "react-native-toast-message";
import BallParkAccountNotFound from "screens/BallparkAccountNotFound/BallparkAccountNotFound";
import BuyGift from "screens/BuyGift/BuyGift";
import ClaimGift from "screens/ClaimGift/ClaimGift";
import ClaimGuestPasses from "screens/ClaimGuestPasses/ClaimGuestPasses";
import FinishSignup from "screens/FinishSignup/FinishSignup";
import Shop from "screens/Shop/Shop";
import ShopPhaseTwo from "screens/ShopPhaseTwo/ShopPhaseTwo";
import { trackPageView } from "utils/analytics";
import { generateThemeColor } from "utils/appUtils";
import AuthenticatedFlow from "./AuthenticatedFlow";
import LoginFlowStack from "./LoginFlow";

export default function Navigation() {
  const routeNameRef = useRef<string>();
  const MyTheme = {
    ...DefaultTheme,
    colors: {
      ...DefaultTheme.colors,
      background: "white",
    },
  };
  return (
    <NavigationContainer
      linking={linking}
      ref={navigationRef}
      documentTitle={{
        formatter: (options, route) =>
          `${options?.title ?? route?.name} - FanRally`,
      }}
      onReady={() => {
        routeNameRef.current = navigationRef.getCurrentRoute()!.name;
      }}
      onStateChange={async () => {
        if (isDev) return;
        const previousRouteName = routeNameRef.current;
        const currentRouteName = navigationRef.getCurrentRoute()!.name;

        if (previousRouteName !== currentRouteName) {
          // The line below uses the expo-firebase-analytics tracker
          // https://docs.expo.io/versions/latest/sdk/firebase-analytics/
          // Change this line to use another Mobile analytics SDK
          trackPageView();
        }
        // Save the current route name for later comparison
        routeNameRef.current = currentRouteName;
      }}
      theme={MyTheme}
    >
      <RootNavigator></RootNavigator>
    </NavigationContainer>
  );
}

const Stack = createNativeStackNavigator<RootStackParamList>();

function RootNavigator() {
  const storedUserId = useAppSelector((state) => state.auth.id);
  const theme = useAppSelector((state) => state.theme.theme);
  const { tenantThemeColor, tenantSubdomain } = useAppSelector(
    (state) => state.tenant
  );
  const { isRunningInBallpark } = useIsRunningInBallpark();
  const dispatch = useAppDispatch();

  const customTheme = generateThemeColor(tenantThemeColor);
  const initCustomTheme = () => {
    dispatch(setTheme(customTheme));
  };

  const isUserLoggedIn = useMemo(() => {
    // If app is running inside the Ballpark app
    if (isRunningInBallpark) {
      return true;
    }
    return storedUserId !== undefined;
  }, [storedUserId, isRunningInBallpark, tenantSubdomain]);

  useEffect(() => {
    initCustomTheme(); // tenant theme
  }, []);

  return (
    <Stack.Navigator
      screenOptions={{ headerShown: false }}
      screenListeners={{
        state() {
          toast.hide();
        },
      }}
    >
      {isUserLoggedIn ? (
        <Stack.Screen name="AuthenticatedFlow" component={AuthenticatedFlow} />
      ) : (
        <Stack.Screen
          name="LoginFlow"
          component={LoginFlowStack}
          options={{ title: "Login" }}
        />
      )}
      <Stack.Screen
        name="GroupReservation"
        component={GroupReservation}
        options={{ title: "Group reserve" }}
      />
      <Stack.Screen
        name="ClaimSeats"
        component={ClaimGuestPasses}
        options={{ title: "Claim seats" }}
      />
      <Stack.Screen
        name="ClaimGuestPasses"
        component={ClaimGuestPasses}
        options={{ title: "Claim guest passes" }}
      />
      <Stack.Screen name="Legal" component={Legal} />
      <Stack.Screen
        name="Shop"
        component={Shop}
        options={{ title: "Shop plans" }}
      />
      <Stack.Screen
        name="ShopPagePhaseTwo"
        component={ShopPhaseTwo}
        options={{ title: "Shop plans" }}
      />
      <Stack.Screen
        name="FinishSignup"
        component={FinishSignup}
        options={{ title: "Finish signup" }}
      />
      <Stack.Screen
        name="BallparkAccountNotFound"
        component={BallParkAccountNotFound}
        options={{ title: "Ballpark Account Not Found" }}
      />
      <Stack.Screen
        name="BuyGift"
        component={BuyGift}
        options={{ title: "Buy Gift" }}
      />
      <Stack.Screen
        name="ClaimGift"
        component={ClaimGift}
        options={{ title: "Claim Gift" }}
      />
    </Stack.Navigator>
  );
}
